// Each kind of board drawn small and filled in (Features 4): for the first question's cards and every empty
// board's first screen. Drawn from the theme's own tokens (onboarding.css), so a picture reads on Paper and on
// the dark scenes alike, and with no numbers or words: shapes for what would be there, never a made-up figure.

import type { BoardKind } from "../../shared/types";

/** A line of words, drawn as a bar. */
const L = ({ x, y, w, c = "p-ln", h = 4 }: { x: number; y: number; w: number; c?: string; h?: number }) => <rect className={c} x={x} y={y} width={w} height={h} rx={h / 2} />;
/** A tile or a card. */
const T = ({ x, y, w, h, c = "p-cd", r = 5 }: { x: number; y: number; w: number; h: number; c?: string; r?: number }) => <rect className={c} x={x} y={y} width={w} height={h} rx={r} />;
/** A box to tick, ticked or not. */
const Tick = ({ x, y, on }: { x: number; y: number; on?: boolean }) => (
  <g>
    <rect className={on ? "p-ok" : "p-bx"} x={x} y={y} width={7} height={7} rx={2} />
    {on && <path className="p-tk" d={`M${x + 1.6} ${y + 3.6} l1.6 1.6 l2.6 -3`} />}
  </g>
);

function Canvas() {
  return (
    <>
      {/* a number with its sparkline */}
      <T x={10} y={12} w={70} h={52} />
      <L x={18} y={21} w={26} c="p-mt" />
      <rect className="p-hd" x={18} y={31} width={36} height={9} rx={2} />
      <path className="p-c1" d="M18 55 C26 52 30 46 38 48 S52 40 62 42 S70 36 72 36" />
      {/* a chart */}
      <T x={88} y={12} w={142} h={70} />
      <L x={96} y={21} w={34} c="p-mt" />
      <path className="p-area" d="M96 74 L96 60 C110 56 118 62 132 52 S156 44 170 46 S196 34 222 30 L222 74 Z" />
      <path className="p-c1" d="M96 60 C110 56 118 62 132 52 S156 44 170 46 S196 34 222 30" />
      {/* a list with bars */}
      <T x={10} y={72} w={70} h={58} />
      <L x={18} y={81} w={24} c="p-mt" />
      {[54, 40, 30, 20].map((w, i) => (
        <rect key={i} className="p-acs" x={18} y={91 + i * 9} width={w} height={5} rx={2} />
      ))}
      {/* tasks */}
      <T x={88} y={90} w={142} h={40} />
      <Tick x={96} y={99} on />
      <L x={108} y={100.5} w={64} />
      <Tick x={96} y={113} />
      <L x={108} y={114.5} w={88} />
    </>
  );
}

function Doc() {
  return (
    <>
      <T x={10} y={12} w={52} h={118} c="p-side" />
      <L x={18} y={22} w={30} c="p-hd" />
      <L x={22} y={33} w={30} />
      <L x={22} y={43} w={24} c="p-ac" />
      <L x={22} y={53} w={28} />
      <L x={18} y={65} w={32} />
      <T x={70} y={12} w={160} h={118} c="p-sf" r={6} />
      <L x={82} y={24} w={84} h={7} c="p-hd" />
      <L x={82} y={40} w={134} />
      <L x={82} y={49} w={120} />
      <L x={82} y={58} w={128} />
      <Tick x={82} y={68} on />
      <L x={94} y={69.5} w={70} />
      <Tick x={82} y={80} />
      <L x={94} y={81.5} w={92} />
      {/* a live widget inside the page */}
      <T x={82} y={94} w={136} h={26} c="p-cd" r={4} />
      <path className="p-c1" d="M90 114 C100 110 106 104 116 106 S134 98 146 101 S168 96 178 98 S200 92 210 93" />
    </>
  );
}

function Sheet() {
  const cols = [34, 82, 130, 178];
  return (
    <>
      <T x={10} y={12} w={220} h={118} c="p-sf" r={6} />
      {/* the formula bar */}
      <rect className="p-field" x={18} y={20} width={204} height={10} rx={3} />
      <L x={22} y={23} w={14} c="p-mt" h={4} />
      <L x={44} y={23} w={60} h={4} />
      {/* letters, then rows */}
      {cols.map((x, k) => (
        <L key={k} x={x + 14} y={38} w={10} c="p-mt" h={3} />
      ))}
      {[0, 1, 2, 3, 4, 5].map((r) => (
        <g key={r}>
          <L x={20} y={50 + r * 12} w={6} c="p-mt" h={3} />
          <line className="p-rule" x1={30} x2={222} y1={46 + r * 12} y2={46 + r * 12} />
        </g>
      ))}
      {[0, 1, 2, 3, 4].map((r) => (
        <g key={r}>
          <L x={36} y={50 + r * 12} w={30 - (r % 3) * 6} c={r === 0 ? "p-hd" : "p-ln"} h={3} />
          <L x={84} y={50 + r * 12} w={22 + (r % 2) * 10} c={r === 0 ? "p-hd" : "p-ln"} h={3} />
          <L x={150 - (r % 2) * 6} y={50 + r * 12} w={16 + (r % 2) * 6} c={r === 0 ? "p-hd" : "p-ln"} h={3} />
          <L x={198 - (r % 3) * 4} y={50 + r * 12} w={16 + (r % 3) * 4} c={r === 0 ? "p-hd" : "p-ln"} h={3} />
        </g>
      ))}
      {/* the active cell */}
      <rect className="p-ring" x={176} y={95} width={46} height={11} rx={1.5} />
      <L x={150} y={116} w={66} c="p-mt" h={3} />
    </>
  );
}

function Slides() {
  return (
    <>
      <T x={10} y={12} w={44} h={118} c="p-side" />
      {[0, 1, 2, 3].map((n) => (
        <rect key={n} className={n === 0 ? "p-thumb p-is-on" : "p-thumb"} x={17} y={20 + n * 27} width={30} height={19} rx={2.5} />
      ))}
      <T x={62} y={12} w={168} h={118} c="p-sf" r={6} />
      <rect className="p-slide" x={74} y={22} width={144} height={81} rx={3} />
      <L x={86} y={34} w={14} c="p-ac" h={3} />
      <L x={86} y={42} w={86} c="p-hd" h={8} />
      {[0, 1, 2].map((n) => (
        <g key={n}>
          <circle className="p-mt" cx={88} cy={63 + n * 11} r={1.6} />
          <L x={94} y={61.5 + n * 11} w={[96, 80, 64][n]} h={3.5} />
        </g>
      ))}
      <L x={74} y={112} w={110} c="p-mt" h={3} />
      <L x={74} y={120} w={70} c="p-mt" h={3} />
    </>
  );
}

function Plan() {
  return (
    <>
      <T x={10} y={12} w={64} h={118} c="p-side" />
      <L x={18} y={22} w={40} c="p-hd" h={5} />
      <rect className="p-field" x={18} y={32} width={48} height={4} rx={2} />
      <rect className="p-ok" x={18} y={32} width={30} height={4} rx={2} />
      {/* the route: done, building, then planned */}
      <line className="p-spine-ok" x1={24} x2={24} y1={50} y2={72} />
      <line className="p-spine" x1={24} x2={24} y1={72} y2={116} />
      {[0, 1, 2, 3].map((n) => (
        <g key={n}>
          <circle className={n < 2 ? "p-ok" : n === 2 ? "p-stop p-is-on" : "p-stop"} cx={24} cy={50 + n * 22} r={4.5} />
          <L x={34} y={48 + n * 22} w={[30, 24, 32, 22][n]} c={n === 2 ? "p-hd" : "p-ln"} />
        </g>
      ))}
      <T x={82} y={12} w={148} h={118} c="p-sf" r={6} />
      <rect className="p-pill p-is-on" x={94} y={22} width={34} height={9} rx={4.5} />
      <L x={94} y={38} w={74} h={7} c="p-hd" />
      <rect className="p-field" x={94} y={52} width={124} height={4} rx={2} />
      <rect className="p-ok" x={94} y={52} width={56} height={4} rx={2} />
      <Tick x={94} y={64} on />
      <L x={106} y={65.5} w={70} />
      <Tick x={94} y={76} on />
      <L x={106} y={77.5} w={88} />
      <Tick x={94} y={88} />
      <L x={106} y={89.5} w={60} />
      <L x={94} y={104} w={120} c="p-mt" />
      <L x={94} y={113} w={96} c="p-mt" />
    </>
  );
}

function Files() {
  return (
    <>
      <T x={10} y={12} w={58} h={118} c="p-side" />
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <g key={n}>
          <rect className={n === 0 || n === 3 ? "p-acs" : "p-mt"} x={n === 0 || n === 3 ? 16 : 22} y={21 + n * 12} width={6} height={5} rx={1.2} />
          <L x={n === 0 || n === 3 ? 26 : 32} y={21.5 + n * 12} w={[30, 24, 26, 28, 20, 24][n]} c={n === 2 ? "p-ac" : "p-ln"} />
        </g>
      ))}
      <T x={76} y={12} w={102} h={118} c="p-sf" r={6} />
      <L x={86} y={24} w={56} h={7} c="p-hd" />
      <L x={86} y={40} w={80} />
      <L x={86} y={49} w={30} />
      <L x={120} y={49} w={28} c="p-ac" />
      <L x={86} y={58} w={74} />
      <L x={86} y={67} w={60} />
      <L x={86} y={82} w={34} c="p-hd" />
      <L x={86} y={93} w={78} />
      <L x={86} y={102} w={66} />
      <T x={186} y={12} w={44} h={118} c="p-side" />
      <g className="p-graph">
        <path d="M208 64 L194 46 M208 64 L222 48 M208 64 L196 86 M208 64 L222 84 M196 86 L204 108" />
      </g>
      <circle className="p-ac" cx={208} cy={64} r={4.5} />
      {[
        [194, 46],
        [222, 48],
        [196, 86],
        [222, 84],
        [204, 108],
      ].map(([x, y]) => (
        <circle key={`${x}${y}`} className="p-node" cx={x} cy={y} r={3} />
      ))}
    </>
  );
}

function MindMap() {
  const kids: [number, number, string][] = [
    [176, 34, "p-c1"],
    [180, 70, "p-c2"],
    [176, 106, "p-c3"],
    [64, 44, "p-c4"],
    [64, 96, "p-c5"],
  ];
  return (
    <>
      {kids.map(([x, y, c]) => (
        <path key={`${x}${y}`} className={c} d={x > 120 ? `M138 70 C156 70 152 ${y} ${x} ${y}` : `M102 70 C84 70 88 ${y} ${x} ${y}`} />
      ))}
      <path className="p-c1" d="M214 34 C220 34 220 22 228 22" />
      <path className="p-c1" d="M214 34 C220 34 220 44 228 44" />
      <path className="p-c4" d="M28 44 C22 44 22 34 14 34" />
      <rect className="p-core" x={98} y={58} width={44} height={24} rx={8} />
      <L x={108} y={68} w={24} c="p-core-ln" />
      {kids.map(([x, y]) => (
        <g key={`n${x}${y}`}>
          <rect className="p-cd" x={x > 120 ? x : x - 36} y={y - 8} width={38} height={16} rx={6} />
          <L x={(x > 120 ? x : x - 36) + 7} y={y - 2} w={22} />
        </g>
      ))}
    </>
  );
}

function Agenda() {
  // a week: a small month and the calendars down the side, the hours across the days, an event on most of them and
  // the now line on today
  const events: [number, number, number, string][] = [
    [0, 30, 16, "p-ac"],
    [1, 46, 26, "p-meet"],
    [2, 34, 12, "p-ac"],
    [3, 70, 22, "p-ac"],
    [4, 42, 30, "p-meet"],
    [5, 88, 16, "p-ac"],
  ];
  return (
    <>
      <T x={10} y={12} w={54} h={118} c="p-side" />
      {Array.from({ length: 35 }, (_, i) => (
        <rect key={i} className={i === 17 ? "p-ac" : "p-mt"} x={17 + (i % 7) * 6} y={21 + Math.floor(i / 7) * 6} width={3} height={3} rx={1} />
      ))}
      {[0, 1, 2].map((n) => (
        <g key={n}>
          <rect className={["p-ac", "p-ok", "p-dr"][n]} x={17} y={62 + n * 12} width={6} height={6} rx={1.5} />
          <L x={27} y={63.25 + n * 12} w={[28, 20, 24][n]} h={3.5} />
        </g>
      ))}
      <T x={72} y={12} w={158} h={118} c="p-sf" r={6} />
      {Array.from({ length: 7 }, (_, d) => (
        <L key={d} x={86 + d * 20} y={19} w={9} c={d === 2 ? "p-hd" : "p-mt"} h={3} />
      ))}
      {[0, 1, 2, 3, 4].map((n) => (
        <g key={n}>
          <L x={77} y={31 + n * 20} w={5} c="p-mt" h={2.5} />
          <line className="p-rule" x1={84} x2={225} y1={32.25 + n * 20} y2={32.25 + n * 20} />
        </g>
      ))}
      {events.map(([d, y, h, c]) => (
        <g key={d}>
          <rect className={c === "p-ac" ? "p-slot" : c} x={85 + d * 20} y={y} width={18} height={h} rx={2.5} />
          {c === "p-ac" && <rect className="p-ac" x={85 + d * 20} y={y} width={2} height={h} rx={1} />}
        </g>
      ))}
      <line className="p-now" x1={125} x2={143} y1={58} y2={58} />
      <circle className="p-now-dot" cx={125} cy={58} r={2.5} />
    </>
  );
}

function Calendar() {
  // a month: a post on some days, each by how far along it is
  const posts: Record<number, string> = { 2: "p-ok", 4: "p-ok", 8: "p-ac", 10: "p-ac", 12: "p-dr", 16: "p-ac", 19: "p-dr", 23: "p-dr" };
  return (
    <>
      <T x={10} y={12} w={220} h={118} c="p-sf" r={6} />
      <L x={18} y={20} w={48} h={6} c="p-hd" />
      {Array.from({ length: 7 }, (_, d) => (
        <L key={d} x={22 + d * 29.4} y={32} w={10} c="p-mt" h={3} />
      ))}
      {Array.from({ length: 28 }, (_, n) => {
        const x = 18 + (n % 7) * 29.4;
        const y = 40 + Math.floor(n / 7) * 22;
        return (
          <g key={n}>
            <rect className="p-cell" x={x} y={y} width={26.4} height={19} rx={3} />
            {posts[n] && <rect className={posts[n]} x={x + 3} y={y + 9} width={20.4} height={6} rx={2} />}
          </g>
        );
      })}
    </>
  );
}

function Pipeline() {
  const counts = [3, 2, 2, 1];
  return (
    <>
      {counts.map((n, c) => (
        <g key={c}>
          <rect className="p-col" x={10 + c * 56} y={12} width={52} height={118} rx={6} />
          <L x={17 + c * 56} y={20} w={22} c="p-hd" />
          {Array.from({ length: n }, (_, r) => (
            <g key={r}>
              <rect className="p-cd" x={15 + c * 56} y={32 + r * 30} width={42} height={25} rx={4} />
              {c === 3 && <rect className="p-ok" x={15 + c * 56} y={32 + r * 30} width={2.5} height={25} rx={1} />}
              <L x={21 + c * 56} y={38 + r * 30} w={24} c="p-hd" h={3.5} />
              <L x={21 + c * 56} y={46 + r * 30} w={16} c="p-mt" h={3} />
            </g>
          ))}
        </g>
      ))}
    </>
  );
}

function Custom() {
  // a tool of its own: a row of choices over a list of records, one with its tag lit, and a form to add another
  return (
    <>
      <T x={10} y={12} w={220} h={118} c="p-sf" r={6} />
      <T x={18} y={20} w={26} h={9} c="p-acs" r={4.5} />
      <T x={48} y={20} w={26} h={9} c="p-cd" r={4.5} />
      <T x={78} y={20} w={26} h={9} c="p-cd" r={4.5} />
      {[0, 1, 2].map((r) => (
        <g key={r}>
          <rect className="p-cd" x={18} y={38 + r * 24} width={204} height={19} rx={4} />
          <L x={26} y={44 + r * 24} w={[70, 58, 82][r]} c="p-hd" h={4} />
          <rect className={r === 1 ? "p-ok" : "p-acs"} x={190} y={43 + r * 24} width={24} height={9} rx={4.5} />
        </g>
      ))}
      <rect className="p-cd" x={18} y={110} width={150} height={14} rx={4} />
      <L x={26} y={115} w={52} c="p-mt" h={4} />
      <rect className="p-acs" x={176} y={110} width={46} height={14} rx={4} />
    </>
  );
}

const DRAW: Record<BoardKind, () => React.ReactNode> = {
  canvas: Canvas,
  doc: Doc,
  sheet: Sheet,
  slides: Slides,
  plan: Plan,
  files: Files,
  mindmap: MindMap,
  agenda: Agenda,
  calendar: Calendar,
  pipeline: Pipeline,
  custom: Custom,
};

/** A kind of board, drawn filled in. Sizes itself to its box; the theme it sits in gives the colours. */
export function KindPicture({ kind, className }: { kind: BoardKind; className?: string }) {
  const Draw = DRAW[kind] ?? Canvas;
  return (
    <svg className={`ob-pic${className ? ` ${className}` : ""}`} viewBox="0 0 240 142" aria-hidden focusable="false">
      <Draw />
    </svg>
  );
}
